import { useState, type ReactNode } from 'react';
import { ScrollView, useWindowDimensions, View } from 'react-native';

import { Badge, Button, Card, Dialog, DialogButton, Input, Menu, MenuItem, Text } from '@/components/ui';
import { cn } from '@/lib/design-system/cn';
import { breakpointFromWidth } from '@/lib/design-system/breakpoints';
import {
  colorTokenNames,
  tokens,
  typographyAt,
  type BreakpointKey,
  type ButtonSize,
  type ButtonVariant,
  type TextVariant,
} from '@/lib/design-system/tokens';
import { textVariantClass } from '@/lib/design-system/variants';

function Section({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <View className="w-full shrink-0 border-b border-border pb-10">
      <Text variant="h2">{title}</Text>
      {description ? (
        <Text variant="caption" className="mt-1">
          {description}
        </Text>
      ) : null}
      <View className="mt-6 w-full shrink-0 flex-col gap-6">{children}</View>
    </View>
  );
}

function SpecimenLabel({ name }: { name: string }) {
  return (
    <Text variant="label" className="mb-2 font-mono">
      {name}
    </Text>
  );
}

function ColorSwatch({ name, value }: { name: string; value: string }) {
  return (
    <View className="w-full shrink-0 flex-row items-center gap-3">
      <View
        className="h-10 w-10 shrink-0 rounded-lg border border-border"
        style={{ backgroundColor: value }}
      />
      <View className="min-w-0 flex-1 flex-col gap-0.5">
        <Text variant="body-sm" className="font-mono">
          {name}
        </Text>
        <Text variant="caption">{value}</Text>
      </View>
    </View>
  );
}

const textVariants: TextVariant[] = ['display', 'h1', 'h2', 'h3', 'body', 'body-sm', 'caption', 'label'];
const buttonVariants: ButtonVariant[] = ['primary', 'secondary', 'ghost', 'danger'];
const buttonSizes: ButtonSize[] = ['sm', 'md', 'lg'];

const breakpointKeys: BreakpointKey[] = ['phone', 'tablet', 'desktop'];

export function DesignSystemShowcase() {
  const { width } = useWindowDimensions();
  const activeBp = breakpointFromWidth(width);
  const breakpointLabel =
    activeBp === 'desktop'
      ? `desktop (≥${tokens.screens.desktop})`
      : activeBp === 'tablet'
        ? `tablet (${tokens.screens.tablet}–${Number.parseInt(tokens.screens.desktop, 10) - 1}px)`
        : `phone (<${tokens.screens.tablet})`;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <ScrollView className="min-h-0 flex-1 bg-surface">
      <View className="mx-auto w-full max-w-content shrink-0 px-page py-page-y">
        <View className="w-full shrink-0 flex-col gap-3">
          <Text variant="display">Couplespace design system</Text>
          <Text variant="body" className="text-fg-muted">
            Living catalog of tokens and components. Reference style names from docs/design_system.md or this
            page when requesting UI changes.
          </Text>
          <View className="self-start rounded-lg bg-accent-muted px-3 py-1">
            <Text variant="caption" className="text-accent">
              Active scale: {breakpointLabel} · {Math.round(width)}px wide
            </Text>
          </View>
        </View>

        <View className="mt-12 w-full shrink-0 flex-col gap-10">
          <Section title="Colors" description="Semantic tokens from design-tokens.json">
            <View className="w-full shrink-0 flex-col gap-4">
              {colorTokenNames.map((name) => (
                <ColorSwatch
                  key={name}
                  name={name}
                  value={tokens.colors[name as keyof typeof tokens.colors]}
                />
              ))}
            </View>
          </Section>

          <Section
            title="Typography"
            description='Text variants — use <Text variant="…"> or cs-text-* classes'
          >
            <View className="w-full shrink-0 flex-col gap-8">
              {textVariants.map((variant) => (
                <View key={variant} className="w-full shrink-0 flex-col">
                  <SpecimenLabel name={`Text variant="${variant}" · ${textVariantClass[variant]}`} />
                  <Text variant={variant}>The quick brown fox jumps over the lazy dog.</Text>
                </View>
              ))}
              <View className="w-full shrink-0 flex-col">
                <SpecimenLabel name='Text muted · className="text-fg-muted"' />
                <Text variant="body" muted>
                  Secondary body copy for descriptions and metadata.
                </Text>
              </View>
            </View>
          </Section>

          <Section
            title="Responsive type scale"
            description="Same variant names at every viewport — sizes come from CSS variables in responsive-vars.css"
          >
            <View className="w-full shrink-0 flex-col gap-3">
              {textVariants.map((variant) => (
                <View key={variant} className="w-full shrink-0 flex-col gap-1 border-b border-border pb-3">
                  <Text variant="body-sm" className="font-mono font-semibold text-fg">
                    {variant}
                  </Text>
                  {breakpointKeys.map((bp) => {
                    const scale = typographyAt(variant, bp);
                    const active = bp === activeBp;
                    return (
                      <Text
                        key={bp}
                        variant="caption"
                        className={active ? 'font-mono text-accent' : 'font-mono'}
                      >
                        {bp}: {scale.fontSize} / {scale.lineHeight}
                        {active ? ' ← active' : ''}
                      </Text>
                    );
                  })}
                </View>
              ))}
            </View>
          </Section>

          <Section title="Buttons" description="Button component — variants and sizes">
            <View className="w-full shrink-0 flex-col gap-6">
              <View className="w-full shrink-0 flex-col gap-3">
                <Text variant="h3">Variants (md)</Text>
                <View className="flex-row flex-wrap gap-2">
                  {buttonVariants.map((variant) => (
                    <Button key={variant} variant={variant} size="md" label={variant} onPress={() => undefined} />
                  ))}
                </View>
              </View>
              <View className="w-full shrink-0 flex-col gap-3">
                <Text variant="h3">Sizes (primary)</Text>
                <View className="flex-row flex-wrap items-center gap-2">
                  {buttonSizes.map((size) => (
                    <Button key={size} variant="primary" size={size} label={size} onPress={() => undefined} />
                  ))}
                </View>
              </View>
              <View className="w-full shrink-0 flex-col gap-3">
                <Text variant="h3">States</Text>
                <View className="flex-row flex-wrap gap-2">
                  <Button variant="primary" label="Loading" loading onPress={() => undefined} />
                  <Button variant="secondary" label="Disabled" disabled onPress={() => undefined} />
                </View>
              </View>
            </View>
          </Section>

          <Section title="Inputs" description="Input component — cs-input">
            <View className="w-full max-w-sm shrink-0 flex-col gap-6">
              <Input label="Label" placeholder="Placeholder text" />
              <Input label="With hint" hint="Helper text below the field." placeholder="Email" />
              <Input label="With error" error="This field is required." defaultValue="invalid@" />
              <Input label="Disabled" disabled placeholder="Disabled" />
            </View>
          </Section>

          <Section title="Badges" description="Badge variants">
            <View className="flex-row flex-wrap gap-2">
              <Badge label="neutral" variant="neutral" />
              <Badge label="accent" variant="accent" />
              <Badge label="danger" variant="danger" />
              <Badge label="success" variant="success" />
              <Badge label="warning" variant="warning" />
            </View>
          </Section>

          <Section title="Cards" description="Card component — cs-card">
            <Card title="Card title" description="Optional description in the header.">
              <Text variant="body">Card body content. Use for grouped settings or summaries.</Text>
            </Card>
          </Section>

          <Section title="Dialog" description="Dialog + DialogButton — cs-dialog-*">
            <Button variant="secondary" label="Open dialog" onPress={() => setDialogOpen(true)} />
            <Dialog
              open={dialogOpen}
              title="Example dialog"
              onClose={() => setDialogOpen(false)}
              footer={
                <>
                  <DialogButton label="Cancel" onPress={() => setDialogOpen(false)} />
                  <DialogButton label="Confirm" variant="primary" onPress={() => setDialogOpen(false)} />
                </>
              }
            >
              <Text variant="body">Dialog body uses cs-dialog-body padding and typography tokens.</Text>
            </Dialog>
          </Section>

          <Section title="Menu" description="Menu + MenuItem — cs-menu-*">
            <View className="w-full shrink-0">
              <Button variant="secondary" label="Open menu" onPress={() => setMenuOpen((o) => !o)} />
              <Menu
                open={menuOpen}
                anchor={{ left: 16, top: 120 }}
                onClose={() => setMenuOpen(false)}
              >
                <MenuItem label="Default item" onPress={() => setMenuOpen(false)} />
                <MenuItem label="Destructive" destructive onPress={() => setMenuOpen(false)} />
                <MenuItem label="Disabled" disabled onPress={() => setMenuOpen(false)} />
              </Menu>
            </View>
          </Section>

          <Section title="Spacing" description="Layout tokens — var(--cs-space-*) updates per breakpoint">
            <View className="w-full shrink-0 flex-col gap-4">
              {Object.entries(tokens.spacing).map(([name, scales]) => (
                <View key={name} className="w-full shrink-0 flex-col gap-1">
                  <Text variant="body-sm" className="font-mono font-semibold">
                    {name}
                  </Text>
                  {breakpointKeys.map((bp) => (
                    <Text key={bp} variant="caption" className="font-mono">
                      {bp}: {scales[bp]}
                    </Text>
                  ))}
                  <View
                    className="mt-1 shrink-0 bg-accent-muted"
                    style={{ width: scales[activeBp], height: 12 }}
                  />
                </View>
              ))}
            </View>
          </Section>

          <Section title="Radius" description="borderRadius tokens">
            <View className="flex-row flex-wrap gap-6">
              {Object.entries(tokens.borderRadius).map(([name, value]) => (
                <View key={name} className="shrink-0 flex-col items-center gap-2">
                  <View
                    className="h-14 w-14 border border-border bg-surface-raised"
                    style={{ borderRadius: value }}
                  />
                  <Text variant="caption" className="font-mono">
                    {name}
                  </Text>
                </View>
              ))}
            </View>
          </Section>

          <Section title="Breakpoints" description="screens in design-tokens.json — align JS layout with these">
            <View className="w-full shrink-0 flex-col gap-2">
              <Text variant="body-sm" className="font-mono">
                phone: &lt; {tokens.screens.tablet}
              </Text>
              <Text variant="body-sm" className="font-mono">
                tablet: {tokens.screens.tablet} – {String(Number.parseInt(tokens.screens.desktop, 10) - 1)}px
              </Text>
              <Text variant="body-sm" className="font-mono">
                desktop: ≥ {tokens.screens.desktop}
              </Text>
            </View>
            <View
              className={cn(
                'w-full shrink-0 rounded-lg border border-border px-4 py-3',
                width >= 1024 ? 'bg-accent-muted' : width >= 640 ? 'bg-warning-muted' : 'bg-surface-raised',
              )}
            >
              <Text variant="body-sm">
                This panel uses accent-muted on desktop, warning-muted on tablet, surface-raised on phone.
              </Text>
            </View>
          </Section>
        </View>
      </View>
    </ScrollView>
  );
}
